import {
  ConnectionError,
  ConnectionErrorReason,
  ConnectionQuality,
  DisconnectReason,
  Room,
  Track,
  VideoPresets,
} from 'livekit-client';
import type { AudioCaptureOptions, VideoCaptureOptions } from 'livekit-client';
import type { CallFile, CallMessage, ChatCallParticipant, ChatPerson } from '../../api';
import { Facing, InputKind, savedDevice } from './devices';

/*
 * Понятия звонка и всё, что считается без React: состав участников, чтение
 * комнаты LiveKit, настройки съёмки и память полосы участников. Провайдер
 * держит только ход звонка и подписки, а это лежит рядом, чтобы его файл
 * оставался читаемым.
 */

export type CallKind = 'audio' | 'video';
export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'active' | 'ended';

export type CallView = 'full' | 'mini';
/** Сетка равных плиток или говорящий крупно с полосой остальных. */
export type CallLayout = 'grid' | 'speaker';
/** Что показывать: весь экран или окно одного приложения. */
export type ShareSurface = 'monitor' | 'window';
/** Какую дорожку участника просит плитка. */
export type TrackSlot = 'camera' | 'screen' | 'audio';

export type Participant = {
  id: string;
  name: string;
  login: string;
  avatarAt: string | null;
  status: 'ringing' | 'active' | 'left' | 'declined';
  micOn: boolean;
  camOn: boolean;
  screenOn: boolean;
  speaking: boolean;
  hand: boolean;
  isMe: boolean;
  /** Браузер участника умеет показывать экран — ему можно дать право показа. */
  screenCapable: boolean;
};

/*
 * Сигналы звонка ходят каналом данных комнаты — тем же, которым живёт доска.
 * Так их шлёт и гость: запрос к серверу ему недоступен, у него нет учётной
 * записи. Формат отличается от сообщений доски ключом (`s` против `t`),
 * поэтому разборы друг друга не задевают.
 */
export type Reaction = 'up' | 'down' | 'clap';
export type RoomSignal = { s: 'hand'; on: boolean } | { s: 'reaction'; emo: Reaction };

const REACTIONS: Reaction[] = ['up', 'down', 'clap'];

export const encodeSignal = (sig: RoomSignal): Uint8Array =>
  new TextEncoder().encode(JSON.stringify(sig));

/** Чужие байты — данные, а не команда: всё, что не наш сигнал, даёт null. */
export function decodeSignal(bytes: Uint8Array): RoomSignal | null {
  let data: unknown;
  try {
    data = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const m = data as Record<string, unknown>;
  if (m.s === 'hand' && typeof m.on === 'boolean') return { s: 'hand', on: m.on };
  if (m.s === 'reaction' && REACTIONS.includes(m.emo as Reaction)) {
    return { s: 'reaction', emo: m.emo as Reaction };
  }
  return null;
}

/** Состояние звонка без выводимых полей — то, что живёт в useState. */
export type Core = {
  phase: CallPhase;
  kind: CallKind;
  view: CallView;
  layout: CallLayout;
  /** Моя рука поднята. */
  hand: boolean;
  /** Что я показываю: 'monitor' | 'window' | 'browser'; пусто — не показываю. */
  shareSurface: string | null;
  /** Короткое уведомление в шапке (3 с), не конец звонка. */
  notice: string;
  error: string;
  /** Секунды с момента ответа. */
  seconds: number;
  callId: string | null;
  /** Администратор звонка: только он зовёт новых людей. */
  adminId: string | null;
  /** Ведущий — кто вправе показывать экран. */
  presenterId: string | null;
  /** Камера запрещена браузером: кнопка объясняет это и предлагает выход. */
  camBlocked: boolean;
  /** Ведущий доски: только он открывает, загружает и закрывает лист. Всегда сотрудник. */
  boardHostId: string | null;
  /** Держатель права показа экрана — доски не касается, может быть гостем. */
  screenShareHolderId: string | null;
  /** До какого времени идёт отсчёт в шапке (ISO); пусто — отсчёта нет. */
  timerEndsAt: string | null;
  /** Канал звонка команды. */
  channelId: string | null;
  /** Код ссылки комнаты; пусто — звонок без комнаты, звать по ссылке некуда. */
  linkToken: string | null;
  /** Комната встречи календаря: людей зовут через карточку встречи, а не из звонка. */
  meeting: boolean;
  micOn: boolean;
  camOn: boolean;
  screenOn: boolean;
  /** Своё видео зеркалить — фронтальная камера. */
  mirror: boolean;
  speakerId: string;
  /** Качество связи: 3 хорошо … 1 плохо; null — ещё нет данных. */
  quality: 1 | 2 | 3 | null;
  /** Связь прервалась, идёт восстановление. */
  reconnecting: boolean;
  /** Полоса участников во время показа экрана. */
  stripShown: boolean;
  /** Браузер запретил автозапуск звука — людей не слышно, пока не разрешат. */
  audioBlocked: boolean;
  /** Звонок команды не начался: людей больше десяти, нужно выбрать. */
  needPick: boolean;
  /** Растёт на каждое «лист открыт»/«лист закрыт» — знак доске перечитать текущий лист. */
  boardSignal: number;
};

export const EMPTY: Core = {
  phase: 'idle',
  kind: 'audio',
  view: 'full',
  layout: 'grid',
  hand: false,
  shareSurface: null,
  notice: '',
  error: '',
  seconds: 0,
  callId: null,
  adminId: null,
  presenterId: null,
  camBlocked: false,
  boardHostId: null,
  screenShareHolderId: null,
  timerEndsAt: null,
  channelId: null,
  linkToken: null,
  meeting: false,
  micOn: true,
  camOn: false,
  screenOn: false,
  mirror: true,
  speakerId: '',
  quality: null,
  reconnecting: false,
  stripShown: true,
  audioBlocked: false,
  needPick: false,
  boardSignal: 0,
};

/** Показ экрана умеют только настольные браузеры; на iPhone кнопки нет. */
export const screenSupported =
  typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

/** Признаки участника, читаемые из его публикаций в комнате. */
/** `noScreen` — браузер участника сам сказал, что экран показывать не умеет
 * (атрибут `screen` = `0`, спека 2026-09-25-guest-screen-share, раздел 5). */
export type MediaFlags = { micOn: boolean; camOn: boolean; screenOn: boolean; noScreen?: boolean };
export const NO_MEDIA: MediaFlags = { micOn: false, camOn: false, screenOn: false };

/** Дорожки по участнику: камера, экран и звук приходят по отдельности. */
export type TrackMap = Map<string, Partial<Record<TrackSlot, Track>>>;

export type RoomSnapshot = {
  tracks: TrackMap;
  flags: Record<string, MediaFlags>;
  /** Мои признаки: их же показывает панель кнопок. */
  mine: MediaFlags;
};

/**
 * Совпадают ли карты дорожек. Плитки перецепляют элементы только когда
 * дорожка правда сменилась: mute, уровень громкости и качество связи идут
 * теми же событиями комнаты, а перецепка на каждое из них — мигающее видео.
 */
export function sameTracks(a: TrackMap, b: TrackMap): boolean {
  if (a.size !== b.size) return false;
  for (const [id, slots] of a) {
    const other = b.get(id);
    if (!other) return false;
    if (slots.camera !== other.camera || slots.screen !== other.screen || slots.audio !== other.audio)
      return false;
  }
  return true;
}

const SLOTS: TrackSlot[] = ['camera', 'screen', 'audio'];

/**
 * Ключи дорожек по слотам: `${участник}:${слот}` → метка дорожки. Плитка
 * следит за своим ключом и перецепляет элемент, только когда сменилась её
 * собственная дорожка: общий счётчик заставлял бы всех отцеплять видео,
 * стоило кому-то одному включить камеру.
 */
export function trackKeys(tracks: TrackMap): Record<string, string> {
  const keys: Record<string, string> = {};
  for (const [id, slots] of tracks) {
    for (const slot of SLOTS) {
      const track = slots[slot];
      if (track) keys[`${id}:${slot}`] = track.sid ?? '';
    }
  }
  return keys;
}

const slotOf = (source: Track.Source): TrackSlot | null =>
  source === Track.Source.Camera
    ? 'camera'
    : source === Track.Source.ScreenShare
      ? 'screen'
      : source === Track.Source.Microphone
        ? 'audio'
        : null;

type Publisher = {
  identity: string;
  attributes?: Record<string, string>;
  getTrackPublications: () => { source: Track.Source; isMuted: boolean; track?: Track }[];
};

/**
 * Снимок комнаты целиком. Считать заново дешевле, чем править по кусочкам:
 * публикация, подписка, выключенный микрофон и уход участника меняют одну и
 * ту же картину, а по кусочкам её легко рассинхронизировать.
 */
export function readRoom(room: Room): RoomSnapshot {
  const tracks: TrackMap = new Map();
  const flags: Record<string, MediaFlags> = {};
  const scan = (p: Publisher) => {
    const slots: Partial<Record<TrackSlot, Track>> = {};
    const f: MediaFlags = { ...NO_MEDIA };
    for (const pub of p.getTrackPublications()) {
      const slot = slotOf(pub.source);
      if (!slot) continue;
      // дорожка появится позже публикации — плитка перезапросит её сама
      if (pub.track) slots[slot] = pub.track;
      // «включено» — это живая публикация, а не подписка: подписки может ещё
      // не быть, и если прятать за это плитку, она никогда не подпишется
      const on = !pub.isMuted;
      if (slot === 'audio') f.micOn = on;
      if (slot === 'camera') f.camOn = on;
      if (slot === 'screen') f.screenOn = on;
    }
    // молчание — «умеет»: старые вкладки атрибута не ставят
    if (p.attributes?.screen === '0') f.noScreen = true;
    tracks.set(p.identity, slots);
    flags[p.identity] = f;
  };
  room.remoteParticipants.forEach(scan);
  scan(room.localParticipant);
  return { tracks, flags, mine: flags[room.localParticipant.identity] ?? NO_MEDIA };
}

/**
 * Почему разговор оборвался.
 *
 * Причину приносит сама библиотека (`RoomEvent.Disconnected`), а слой раньше
 * показывал на всё одно «Связь потеряна»: по этой строке нельзя было понять
 * ни что случилось, ни что чинить, — из-за чего оборванный вход в комнату
 * встречи и остался неразобранным.
 */
export function disconnectText(reason?: DisconnectReason): string {
  switch (reason) {
    case DisconnectReason.DUPLICATE_IDENTITY:
      return 'Вы вошли в этот звонок на другом устройстве';
    case DisconnectReason.SERVER_SHUTDOWN:
      return 'Сервер звонков перезапускается — войдите снова';
    case DisconnectReason.PARTICIPANT_REMOVED:
      return 'Вас отключили от звонка';
    case DisconnectReason.ROOM_DELETED:
    case DisconnectReason.ROOM_CLOSED:
      return 'Звонок завершён';
    case DisconnectReason.JOIN_FAILURE:
      return 'Не удалось войти в комнату звонка';
    case DisconnectReason.SIGNAL_CLOSE:
      return 'Связь с сервером звонков потеряна';
    default:
      return 'Связь потеряна';
  }
}

/**
 * Почему не удалось войти в комнату.
 *
 * Библиотека сообщает и причину, и код ответа медиасервера: пропуск не принят —
 * это 401, недоступный адрес — это отдельная причина. Отказ браузера в доступе
 * к микрофону приходит сюда же обычной DOMException.
 */
/**
 * Отказ в микрофоне или камере — не повод выходить из звонка: человек всё
 * равно слышит остальных и может включить устройство позже, разрешив доступ.
 * Ошибки самой связи так не лечатся, они звонок и заканчивают.
 */
export function deviceRefusal(e: unknown): boolean {
  return e instanceof Error && ['NotAllowedError', 'NotFoundError', 'NotReadableError'].includes(e.name);
}

/**
 * Браузер запретил устройство насовсем: человек однажды нажал «не разрешать»,
 * и второй раз спрашивать он уже не станет. Safari запоминает такой отказ для
 * сайта, поэтому включить камеру кнопкой больше нельзя — сначала разрешение
 * (замечание Ильи 15.09.2026).
 */
export function deviceDenied(e: unknown): boolean {
  return e instanceof Error && e.name === 'NotAllowedError';
}

/** Что сказать человеку про недоступное устройство: коротко и с выходом. */
export function deviceHint(e: unknown, device: 'камера' | 'микрофон'): string {
  if (deviceDenied(e)) return `Доступ к ${device === 'камера' ? 'камере' : 'микрофону'} запрещён в настройках браузера для этого сайта`;
  if (e instanceof Error && e.name === 'NotFoundError') return `${device === 'камера' ? 'Камера' : 'Микрофон'} не найдена на устройстве`.replace('найдена', device === 'камера' ? 'найдена' : 'найден');
  return `${device === 'камера' ? 'Камера' : 'Микрофон'} занята другой программой`.replace('занята', device === 'камера' ? 'занята' : 'занят');
}

export function connectText(e: unknown): string {
  if (e instanceof ConnectionError) {
    switch (e.reason) {
      case ConnectionErrorReason.NotAllowed:
        return `Сервер звонков не принял пропуск (${e.status ?? 401})`;
      case ConnectionErrorReason.ServerUnreachable:
      case ConnectionErrorReason.WebSocket:
      case ConnectionErrorReason.ServiceNotFound:
        return 'Сервер звонков недоступен';
      case ConnectionErrorReason.Timeout:
        return 'Сервер звонков не ответил';
      default:
        return `Не удалось войти в звонок: ${e.message}`;
    }
  }
  if (e instanceof Error && e.name === 'NotAllowedError') return 'Браузер не дал доступ к микрофону';
  if (e instanceof Error && e.name === 'NotFoundError') return 'Микрофон не найден';
  if (e instanceof Error && e.name === 'NotReadableError') return 'Микрофон занят другой программой';
  return e instanceof Error && e.message ? e.message : 'Не удалось войти в звонок';
}

/**
 * Сорванный вход в звонок — одно место на все ветки.
 *
 * Серверу говорят «вхожу» раньше, чем начинается подключение к медиасерверу:
 * запрос входа уходит первым, и после него человек уже числится в звонке. Если
 * дальше вход сорвался — пропуск не принят, медиасервер недоступен, браузер не
 * дал микрофон, — сервер обязан узнать об уходе, иначе вызываемому звонит всю
 * минуту дозвона, в переписке остаётся «положили трубку» вместо «пропущенного»,
 * в групповом звонке висит плитка того, кого там нет, а комната встречи держится
 * «активной»: её правило завершения требует ноль активных участников.
 *
 * Обработчик разрыва комнаты на время подключения молчит (он перебил бы
 * настоящую причину отказа), поэтому сообщить об уходе больше некому.
 * Возвращает текст для экрана — сообщение об уходе уже отправлено.
 */
export function failedJoin(input: {
  /** Звонок, о входе в который сервер уже знает; null — сказать ещё нечего. */
  callId: string | null;
  error: unknown;
  leave: (callId: string) => Promise<unknown>;
}): string {
  if (input.callId) void input.leave(input.callId).catch(() => {});
  return connectText(input.error);
}

/**
 * Событие не про тот звонок, в котором эта вкладка, — его надо пропустить.
 *
 * Проверка на совпадение номеров была и раньше, но она молчала, когда у
 * вкладки звонка нет вовсе (баг Ильи 17.09.2026). А именно так и выходит в
 * самый неподходящий момент: начало нового звонка первым делом завершает на
 * сервере прошлый, повисший от оборванного разговора, и событие «звонок
 * завершён» про тот, прошлый, прилетает раньше, чем ответ с номером нового.
 * Вкладка принимала его на свой счёт и гасила едва начатый звонок: экран
 * пропадал, собеседник брал трубку и не заставал никого.
 *
 * Поэтому события, которые звонок заканчивают, без известного номера своего
 * звонка не действуют: заканчивать нечего. Остальные (вошёл, отказался)
 * безобидны и проходят, как раньше, — состав всё равно перечитывается.
 */
const ENDING = ['end', 'closed', 'kicked'];

export function foreignCallEvent(type: string, eventCallId: string | undefined, myCallId: string | null): boolean {
  if (type === 'invite') return false;
  if (eventCallId && myCallId) return eventCallId !== myCallId;
  return !myCallId && ENDING.includes(type);
}

/**
 * Занята ли вкладка разговором. Не «есть номер звонка», а «есть живой экран»
 * (баг Ильи 21.09.2026): номер мог остаться от разговора, который кончился
 * незаметно, и по нему вкладка молча отклоняла все входящие — люди
 * переставали видеть звонки друг друга.
 */
export function callBusy(phase: CallPhase, callId: string | null): boolean {
  return !!callId && phase !== 'idle' && phase !== 'ended';
}

export type OwnJoin = 'mine' | 'close' | 'ignore' | 'elsewhere';

/**
 * Сервер сказал, что я вошёл в звонок. Что делать этой вкладке (спека
 * 2026-10-02-tabs-devices-sync, раздел 2):
 * - 'mine' — вошла эта вкладка (или старая вкладка без метки, уже в комнате);
 * - 'close' — эта вкладка звенит, трубку взяли в другой: экран молча гаснет;
 * - 'ignore' — вкладка в звонке не участвует: звоню сам из соседней;
 * - 'elsewhere' — вкладка в разговоре, а вошли с другого устройства:
 *   прежнее поведение, «идущий разговор везде» в работу не взят.
 */
export function ownJoin(phase: CallPhase, eventTabId: string | undefined, myTabId: string, inRoom: boolean): OwnJoin {
  if (eventTabId && eventTabId === myTabId) return 'mine';
  if (phase === 'incoming') return 'close';
  if (phase === 'idle' || phase === 'ended') return 'ignore';
  return eventTabId || !inRoom ? 'elsewhere' : 'mine';
}

/**
 * Насколько свежим должен быть звонок, чтобы вкладка подхватила его входящим.
 * Дозвон на сервере живёт минуту (RING_TIMEOUT_MS); запас — на разницу часов
 * и на дорогу события.
 */
export const PICKUP_FRESH_MS = 90_000;

/**
 * След оборванного разговора, а не приглашение: строка «звонит», пережившая
 * срок дозвона. Показывать по ней входящий нельзя — такой экран запоминал
 * номер мёртвого звонка, и следующие приглашения вкладка молча отклоняла.
 * `startedAt` — секунды, как их отдаёт сервер.
 */
export function ringingTooOld(startedAt: number, now = Date.now()): boolean {
  return now - startedAt * 1000 > PICKUP_FRESH_MS;
}

/**
 * Висящий «Входящий звонок» — метка `call-<номер>`. Открытое приложение
 * убирает только такие: «Итоги звонка» (`call-summary-`) и «Пропущенный
 * звонок» (`call-missed-`, его ставит sw.js) человек должен увидеть.
 */
export function isRingingCallTag(tag: string): boolean {
  return tag.startsWith('call-') && !tag.startsWith('call-summary-') && !tag.startsWith('call-missed-');
}

/** Три деления качества по оценке самой библиотеки. */
export const levelOf = (q: ConnectionQuality): 1 | 2 | 3 | null =>
  q === ConnectionQuality.Excellent || q === ConnectionQuality.Good
    ? 3
    : q === ConnectionQuality.Poor
      ? 2
      : q === ConnectionQuality.Lost
        ? 1
        : null;

export function micOptions(): AudioCaptureOptions {
  const id = savedDevice('audioinput');
  return { echoCancellation: true, noiseSuppression: true, ...(id ? { deviceId: { ideal: id } } : {}) };
}

/** Выбранная камера важнее стороны: сторона нужна только телефону. */
export function camOptions(facing: Facing, byFacing = false): VideoCaptureOptions {
  const id = byFacing ? null : savedDevice('videoinput');
  return {
    resolution: VideoPresets.h720.resolution,
    ...(id ? { deviceId: { ideal: id } } : { facingMode: facing }),
  };
}

const STRIP_KEY = 'code-call-strip';

/** Полоса участников: на вебе по умолчанию видна, на телефоне закрывает половину экрана. */
export function readStrip(mobile: boolean): boolean {
  try {
    const v = localStorage.getItem(STRIP_KEY);
    return v === null ? !mobile : v === '1';
  } catch {
    return !mobile;
  }
}

export function saveStrip(shown: boolean): void {
  try {
    localStorage.setItem(STRIP_KEY, shown ? '1' : '0');
  } catch {
    /* без памяти выбор проживёт до конца звонка */
  }
}

export type CallState = Core & {
  participants: Participant[];
  /** Громкость каждого собеседника по автомикшеру, 0–1 (спека 2026-09-25-call-automixer). */
  gains: Record<string, number>;
};

export type CallCtx = CallState & {
  me: string;
  isAdmin: boolean;
  /** Вошёл по ссылке для гостей, а не сотрудник: за его звонком нет приложения. */
  isGuest: boolean;
  /** Я ведущий или держатель права показа, звонок активен, устройство умеет показывать экран, никто другой не показывает. */
  canShare: boolean;
  screenSupported: boolean;
  /** Отдать плитке дорожку участника; возвращает отсоединение. */
  attach: (el: HTMLMediaElement, userId: string, source: TrackSlot) => () => void;
  /** Метка дорожки каждого слота: по своей плитка понимает, что пора перецепиться. */
  trackKeys: Record<string, string>;
  /** Разрешить звук после запрета автозапуска — только из жеста человека. */
  resumeAudio: () => Promise<void>;
  /** Живой слой доски (раздел 4.2 спеки): отправить данные всем в комнате по каналу данных медиасервера. */
  sendBoardData: (bytes: Uint8Array) => void;
  /** Подписаться на входящие данные канала; возвращает отписку. */
  onBoardData: (handler: (bytes: Uint8Array, from: string) => void) => () => void;
  call: (person: ChatPerson, kind: CallKind) => Promise<void>;
  callChannel: (channelId: string, kind: CallKind) => Promise<void>;
  /** Звонок списку людей: канал больше десяти человек, состав выбрали руками. */
  callPeople: (userIds: string[], kind: CallKind) => Promise<void>;
  invite: (userIds: string[]) => Promise<void>;
  /**
   * Удалить гостя из звонка; право администратора. Идентификатор — строка
   * участника, а не имя в комнате: у гостя это `id` без приставки `guest:`.
   */
  kick: (participantId: string) => Promise<void>;
  /** Завершить звонок для всех; право администратора. */
  closeRoom: () => Promise<void>;
  /** Готовый адрес ссылки на комнату; пусто — звонок без комнаты. */
  link: string;
  accept: () => Promise<void>;
  /** Войти в постоянную комнату звонка встречи календаря (у неё нет карточки звонка на сервере). */
  joinMeeting: (eventId: string) => Promise<void>;
  decline: () => void;
  hangup: (successorId?: string) => void;
  toggleMic: () => void;
  toggleCam: () => void;
  toggleHand: () => void;
  /** Послать реакцию: её видят все, включая гостя, и она гаснет через три секунды. */
  sendReaction: (emo: Reaction) => void;
  /** Реакция, горящая сейчас: одна на весь звонок; пусто — реакции нет. */
  reaction: { from: string; emo: Reaction } | null;
  /** Переписка звонка: лента, счётчик непрочитанного и действия над ней. */
  messages: CallMessage[];
  unread: number;
  loadMessages: () => Promise<void>;
  sendMessage: (text: string, fileIds?: number[]) => Promise<void>;
  uploadMessageFile: (file: File) => Promise<CallFile>;
  markChatRead: () => void;
  startScreen: (surface?: ShareSurface) => Promise<void>;
  stopScreen: () => void;
  /** Передать роль ведущего: показ экрана и доска идут за ней. */
  setHost: (userId: string) => Promise<void>;
  /** Выдать право показа экрана участнику или вернуть себе (null); право ведущего. */
  setScreenShare: (participantId: string | null) => Promise<void>;
  switchInput: (kind: InputKind, deviceId: string) => Promise<void>;
  switchSpeaker: (deviceId: string) => void;
  flipCamera: () => Promise<void>;
  minimize: () => void;
  expand: () => void;
  setLayout: (layout: CallLayout) => void;
  toggleStrip: () => void;
  /** Выбор людей открыт слоем — снять признак. */
  clearNeedPick: () => void;
};

/** Список для экрана: люди с сервера, медиа из комнаты, рука из сигнала. */
export function buildParticipants(input: {
  roster: ChatCallParticipant[];
  media: Record<string, MediaFlags>;
  speakers: string[];
  hands: Record<string, boolean>;
  me: string;
  mine: Pick<Core, 'micOn' | 'camOn' | 'screenOn' | 'hand'>;
}): Participant[] {
  return input.roster.map((p) => {
    const isMe = p.id === input.me;
    const f = input.media[p.id] ?? NO_MEDIA;
    return {
      id: p.id,
      name: p.name,
      login: p.login,
      avatarAt: p.avatarAt,
      status: p.status,
      micOn: isMe ? input.mine.micOn : f.micOn,
      camOn: isMe ? input.mine.camOn : f.camOn,
      screenOn: isMe ? input.mine.screenOn : f.screenOn,
      speaking: input.speakers.includes(p.id),
      hand: isMe ? input.mine.hand : !!input.hands[p.id],
      isMe,
      screenCapable: !f.noScreen,
    };
  });
}

/** Событие принесло участника, которого ещё нет в составе: дописываем. */
export function applyJoined(
  list: ChatCallParticipant[],
  who: ChatCallParticipant,
): ChatCallParticipant[] {
  return list.some((p) => p.id === who.id) ? list : [...list, who];
}

/**
 * Право показа сменилось, и его получил не я, а свою дорожку показа я в
 * этот момент вёл: решает, погасить ли её. Без этого при отъёме права силой
 * кадр показа оставался на экране прежнего докладчика, и новый показ было
 * не начать — приложение считало, что он всё ещё идёт.
 */
export function shouldStopScreenShare(input: { newPresenterId: string; me: string; screenOn: boolean }): boolean {
  return input.screenOn && input.newPresenterId !== input.me;
}
